import * as React from 'react';
import { usePageSlide } from './usePageSlide';

/**
 * iOS-style horizontal swipe between a fixed, ordered set of tabs. Attach the
 * returned `ref` + `onTouchStart`/`onTouchEnd` to a container element; a clear,
 * mostly-horizontal swipe steps to the previous/next tab.
 *
 * Guardrails: vertical scrolls aren't hijacked (needs |dx| ≥ 60 and clearly more
 * horizontal than vertical); swipes that start on an input, a horizontally-
 * scrollable child, or a [data-no-swipe] element are ignored; no wrap-around.
 * Touch-only, so mouse/desktop is unaffected.
 *
 * When `current` changes the container plays an iPhone-style page slide: the new
 * page eases in from the right when moving forward, from the left when going
 * back. It runs for tab taps too, so both routes into a tab look the same.
 */
export function useSwipeTabs<T extends string>(
  order: readonly T[],
  current: T,
  onChange: (next: T) => void,
) {
  const ref = usePageSlide(order, current);
  const start = React.useRef<{ x: number; y: number; skip: boolean } | null>(null);

  function shouldSkip(target: EventTarget | null): boolean {
    let el = target as HTMLElement | null;
    const stop = ref.current;
    while (el && el !== stop) {
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable) return true;
      if (el.dataset?.noSwipe != null) return true;
      const ox = window.getComputedStyle(el).overflowX;
      if ((ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth + 4) return true;
      el = el.parentElement;
    }
    return false;
  }

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length !== 1) { start.current = null; return; }
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY, skip: shouldSkip(e.target) };
  }

  function onTouchEnd(e: React.TouchEvent) {
    const s = start.current;
    start.current = null;
    if (!s || s.skip) return;
    const idx = order.indexOf(current);
    if (idx < 0) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.6) return;
    if (dx < 0 && idx < order.length - 1) onChange(order[idx + 1]!); // swipe left → next
    else if (dx > 0 && idx > 0) onChange(order[idx - 1]!); // swipe right → prev
  }

  return { ref, onTouchStart, onTouchEnd };
}
